import { VersionConfig } from '@/types/assessment';

export const professionalConfig: VersionConfig = {
  edition: 'professional',
  label: 'Professional Edition',
  tagline: 'Your career isn\'t a ladder. It\'s a portfolio.',
  pathNames: { conservative: 'Grow Where You Are', moderate: 'Strategic Pivot', bold: 'Bet on Yourself' },
  timelines: ['Next 90 Days', '6 Months', '1\u20132 Years', '3\u20135 Years'],
  socialPlatforms: ['LinkedIn', 'TikTok'],
  screens: [
    {
      id: 'skills',
      title: 'How does your brain work?',
      subtitle: 'Pick up to 4 skills that define how you actually operate — not your job title.',
      encouragement: 'These aren\'t job skills. They\'re how you think, decide, and create value.',
      type: 'chips-expandable',
      maxSelections: 4,
      options: [
        { id: 'analytical', label: 'Analytical Thinking', description: 'You break things down to understand them', looksLike: 'You build frameworks, analyze metrics, and find root causes others miss' },
        { id: 'creative', label: 'Creative Thinking', description: 'You imagine new possibilities', looksLike: 'You pitch unconventional ideas, redesign processes, or see what others can\'t' },
        { id: 'communication', label: 'Communication', description: 'You make complex things clear and compelling', looksLike: 'You write decks that land, lead all-hands that energize, or translate between teams' },
        { id: 'leadership', label: 'Leadership', description: 'You step up and set direction', looksLike: 'You build teams, make tough calls, and people follow your lead naturally' },
        { id: 'tech-savvy', label: 'Tech Fluency', description: 'You learn and leverage new tools fast', looksLike: 'You adopted AI tools first, automated your workflow, or build internal tools' },
        { id: 'empathy', label: 'Emotional Intelligence', description: 'You read the room and build trust', looksLike: 'You manage up and down well, navigate politics gracefully, and people open up to you' },
        { id: 'strategy', label: 'Strategic Thinking', description: 'You play the long game', looksLike: 'You connect market trends to org strategy and think 3 moves ahead' },
        { id: 'execution', label: 'Execution', description: 'You ship, period', looksLike: 'You turn vision into action, hit deadlines, and clear blockers without being asked' },
        { id: 'systems-thinking', label: 'Systems Thinking', description: 'You see how everything connects', looksLike: 'You optimize processes, find leverage points, and think in feedback loops' },
        { id: 'influence', label: 'Influence Without Authority', description: 'You move people without a title', looksLike: 'You build buy-in, negotiate outcomes, and shape decisions from any seat at the table' },
        { id: 'coaching', label: 'Developing Others', description: 'You make the people around you better', looksLike: 'You mentor junior team members, give feedback that sticks, and grow future leaders' },
        { id: 'sales', label: 'Persuasion & Selling', description: 'You close deals and open doors', looksLike: 'You sell ideas internally, win clients externally, and know how to ask for what you want' },
      ],
    },
    {
      id: 'strengths',
      title: 'What sets you apart?',
      subtitle: 'Choose up to 5 strengths that have actually driven your career forward.',
      encouragement: 'Think about what your best managers, clients, or colleagues would say about you.',
      type: 'chips-expandable',
      maxSelections: 5,
      options: [
        { id: 'pattern-recognition', label: 'Pattern Recognition', description: 'You see connections others miss', looksLike: 'You predict market shifts, spot talent early, or connect unlikely dots across domains' },
        { id: 'storytelling', label: 'Storytelling', description: 'You make ideas stick through narrative', looksLike: 'Your thought leadership gets shared, your pitches win, your personal brand is strong' },
        { id: 'resilience', label: 'Resilience', description: 'You come back stronger from every setback', looksLike: 'Layoffs, pivots, market crashes — you adapted and came out ahead every time' },
        { id: 'curiosity', label: 'Relentless Curiosity', description: 'You never stop learning', looksLike: 'You attend conferences, read across disciplines, and experiment with new approaches constantly' },
        { id: 'hustle', label: 'Bias to Action', description: 'You make things happen, fast', looksLike: 'Side business + day job + advisory role: you make it all work somehow' },
        { id: 'vision', label: 'Big-Picture Vision', description: 'You see the destination before the map exists', looksLike: 'You set 10-year visions and work backwards to quarterly goals' },
        { id: 'calm-under-pressure', label: 'Calm Under Pressure', description: 'You\'re the steady one when everything\'s on fire', looksLike: 'Crisis management, board meetings, layoffs — people look to you when it matters' },
        { id: 'connector', label: 'People Connector', description: 'You bring the right people together', looksLike: 'You make intros that lead to deals, partnerships, hires, and opportunities' },
        { id: 'negotiator', label: 'Negotiation', description: 'You create outcomes where everyone wins', looksLike: 'You negotiate comp, contracts, and partnerships with confidence and skill' },
        { id: 'domain-expert', label: 'Deep Domain Expertise', description: 'You\'re the go-to person in your field', looksLike: 'People seek your opinion, you speak at events, you have a reputation in your niche' },
      ],
    },
    {
      id: 'interests',
      title: 'What lights you up?',
      subtitle: 'Pick as many as you want. These don\'t have to match your current role.',
      encouragement: 'The gap between "what I do" and "what I love" is where your next move lives.',
      type: 'chips',
      options: [
        { id: 'ai-tech', label: 'AI & Technology' },
        { id: 'arts', label: 'Arts & Design' },
        { id: 'business', label: 'Business & Strategy' },
        { id: 'investing', label: 'Investing & Wealth Building' },
        { id: 'social-impact', label: 'Social Impact' },
        { id: 'health', label: 'Health & Wellness' },
        { id: 'media', label: 'Media & Content' },
        { id: 'coaching', label: 'Coaching & Mentoring' },
        { id: 'real-estate', label: 'Real Estate' },
        { id: 'sustainability', label: 'Sustainability & Climate' },
        { id: 'product', label: 'Product & Innovation' },
        { id: 'data', label: 'Data & Analytics' },
        { id: 'leadership-dev', label: 'Leadership Development' },
        { id: 'education', label: 'Education & Teaching' },
        { id: 'writing', label: 'Writing & Thought Leadership' },
        { id: 'operations', label: 'Operations & Scale' },
      ],
    },
    {
      id: 'inspiration',
      title: 'What\'s shaping your thinking?',
      subtitle: 'The content you engage with says a lot about where you\'re headed.',
      encouragement: 'Books, podcasts, YouTube, newsletters, thought leaders — anything goes.',
      type: 'context',
      options: [],
      allowOpenText: true,
      openTextPlaceholder: 'What podcasts or YouTube channels do you listen to?\nBooks that shaped your career thinking?\nNewsletters, communities, or thought leaders you follow?',
    },
    {
      id: 'aspirations',
      title: 'Industries you\'re watching',
      subtitle: 'Pick up to 3 fields catching your attention right now.',
      encouragement: 'These don\'t have to be where you work — just what you find yourself drawn to.',
      type: 'aspirations',
      maxSelections: 3,
      options: [
        { id: 'ai-ml', label: 'AI & Machine Learning' },
        { id: 'creator-economy', label: 'Creator Economy' },
        { id: 'climate-tech', label: 'Climate Tech' },
        { id: 'healthcare-ai', label: 'Healthcare + AI' },
        { id: 'fintech', label: 'Fintech & Digital Finance' },
        { id: 'consulting', label: 'Consulting & Advisory' },
        { id: 'defi-web3', label: 'DeFi & Web3' },
        { id: 'biotech', label: 'Biotech & Life Sciences' },
        { id: 'saas', label: 'SaaS & Software' },
        { id: 'real-estate-tech', label: 'Real Estate & PropTech' },
        { id: 'edtech', label: 'EdTech & Learning' },
        { id: 'cybersecurity', label: 'Cybersecurity' },
        { id: 'ecommerce', label: 'E-Commerce & DTC' },
        { id: 'clean-energy', label: 'Clean Energy & Infrastructure' },
      ],
    },
    {
      id: 'energy-workstyle',
      title: 'What\'s your energy type?',
      subtitle: 'How do you do your best work? Pick up to 5.',
      encouragement: 'Your energy pattern is more important than your time management system.',
      type: 'chips-expandable',
      maxSelections: 5,
      options: [
        { id: 'deep-focus', label: 'Deep Focus', description: 'Long, uninterrupted blocks of concentration', looksLike: 'Calendar blocked, Slack muted, deep work mode until it\'s done' },
        { id: 'burst-energy', label: 'Burst Energy', description: 'Intense sprints then total recharge', looksLike: 'Ship a feature in a day, take a recovery day — rinse and repeat' },
        { id: 'social-energy', label: 'Social Energy', description: 'Energized by collaboration and people', looksLike: 'Back-to-back 1:1s, workshops, team brainstorms — you leave meetings fired up' },
        { id: 'solo-creator', label: 'Solo Creator', description: 'You produce your best work independently', looksLike: 'Remote work, async communication, independent projects on your own timeline' },
        { id: 'variety-seeker', label: 'Variety Seeker', description: 'You need to switch things up regularly', looksLike: 'Portfolio career, multiple revenue streams, fractional roles across companies' },
        { id: 'steady-builder', label: 'Steady Builder', description: 'Consistent, incremental, compounding progress', looksLike: 'Quarterly goals, daily habits, compounding expertise year over year' },
        { id: 'strategic-thinker', label: 'Strategic Thinker', description: 'You need time to think before you act', looksLike: 'Long walks, journaling, whiteboard sessions before committing to a direction' },
        { id: 'deadline-driven', label: 'Deadline-Driven', description: 'You perform best under pressure', looksLike: 'Nothing happens until the deadline is real — then you\'re unstoppable' },
      ],
    },
    {
      id: 'dream-wednesday',
      title: 'Describe your dream Wednesday',
      subtitle: 'You\'re in the thick of it. What does your ideal middle-of-the-week look like?',
      encouragement: 'Not a vacation day. A day where you think "yeah, this is why I do what I do."',
      type: 'dream-wednesday',
      vibeOptions: [
        { id: 'ceo-vibes', label: 'Decision Maker', description: 'Strategy, decisions, leading the vision and shaping the future', pros: ['Maximum impact on outcomes and direction', 'Leadership legacy that compounds', 'You\'re shaping something bigger than yourself'], cons: ['Always on — hard to ever truly unplug', 'Lonely at the top, especially during hard decisions', 'High stakes for every call you make'] },
        { id: 'creator-vibes', label: 'Creator Vibes', description: 'Building, writing, designing — creating things that didn\'t exist before', pros: ['Creative ownership of your work', 'Personal brand that opens doors', 'Flow states that make work feel effortless'], cons: ['Income can be variable, especially early on', 'Self-promotion is required and constant', 'No safety net if inspiration dries up'] },
        { id: 'advisor-vibes', label: 'Advisor Vibes', description: 'Consulting, coaching, sharing hard-won expertise', pros: ['Leverage decades of experience for premium rates', 'Flexible schedule and client selection', '$200-$500+/hr is realistic for top advisors'], cons: ['Client dependency — feast or famine cycles', 'Building a pipeline takes time and effort', 'You\'re always selling yourself'] },
        { id: 'operator-vibes', label: 'Operator Vibes', description: 'Running the machine — optimizing systems and scaling teams', pros: ['Clear metrics to measure your impact', 'Organization-wide influence', 'Build and develop high-performing teams'], cons: ['Constant firefighting and troubleshooting', 'Organizational politics are unavoidable', 'Less creative freedom than other paths'] },
        { id: 'builder-vibes', label: 'Builder Vibes', description: 'Starting from zero and making something real', pros: ['Full ownership of what you create', 'Unlimited upside if it works', 'Innovation and problem-solving every day'], cons: ['Financial risk — especially in the early years', 'Isolation before you have a team', 'Long runway before seeing returns'] },
        { id: 'portfolio-vibes', label: 'Portfolio Vibes', description: 'Mix of projects, roles, and income streams across different domains', pros: ['Diversified risk and income', 'Variety keeps every week interesting', 'Total autonomy over your time and direction'], cons: ['Context switching is mentally draining', 'No employer benefits — everything self-managed', 'Can feel scattered without a clear identity'] },
      ],
      allowOpenText: true,
      openTextPlaceholder: 'Describe your ideal Wednesday in a few sentences...',
    },
    {
      id: 'values',
      title: 'What would you fight for?',
      subtitle: 'Pick up to 5 values that are non-negotiable at this point in your career.',
      encouragement: 'These aren\'t aspirational. These are the lines you won\'t cross.',
      type: 'chips',
      maxSelections: 5,
      options: [
        { id: 'freedom', label: 'Freedom & Autonomy' },
        { id: 'wealth', label: 'Wealth Building' },
        { id: 'creativity', label: 'Creativity & Expression' },
        { id: 'impact', label: 'Making a Lasting Impact' },
        { id: 'legacy', label: 'Legacy & Meaning' },
        { id: 'growth', label: 'Continuous Growth' },
        { id: 'justice', label: 'Justice & Equity' },
        { id: 'innovation', label: 'Innovation & Disruption' },
        { id: 'balance', label: 'Work-Life Integration' },
        { id: 'influence', label: 'Influence & Authority' },
        { id: 'health', label: 'Health & Longevity' },
        { id: 'family', label: 'Family & Relationships' },
        { id: 'mastery', label: 'Mastery & Expertise' },
      ],
    },
    {
      id: 'pain-points',
      title: 'What\'s not working?',
      subtitle: 'Be honest. We\'ll factor this into your paths.',
      encouragement: 'If everything was perfect, you wouldn\'t be here. That\'s a good thing.',
      type: 'barriers',
      options: [
        { id: 'underpaid', label: 'Underpaid for my skills and impact' },
        { id: 'no-growth', label: 'No clear growth path ahead' },
        { id: 'toxic-culture', label: 'Toxic or misaligned culture' },
        { id: 'bored', label: 'Bored, unchallenged, or going through the motions' },
        { id: 'no-balance', label: 'Work-life balance is shot' },
        { id: 'shrinking-industry', label: 'My industry is shrinking or being disrupted' },
        { id: 'own-boss', label: 'I want to be my own boss' },
        { id: 'imposter', label: 'Imposter syndrome — even at this level' },
        { id: 'ai-anxiety', label: 'AI is changing my field and I\'m not sure where I fit' },
        { id: 'burnout', label: 'Burned out and running on fumes' },
      ],
    },
    {
      id: 'cognitive-style',
      title: 'How do you approach big decisions?',
      subtitle: 'Real scenarios that reveal your decision-making DNA.',
      encouragement: 'Go with your gut. Your instincts have been shaped by real experience.',
      type: 'cognitive-style',
      cognitiveScenarios: [
        {
          id: 'cs-1',
          scenario: 'Your company announces a major restructuring. Your role might change significantly.',
          optionA: { label: 'I\'d analyze the org chart, talk to leadership, and position myself strategically', trait: 'Strategic' },
          optionB: { label: 'I\'d start exploring external opportunities immediately — always be ready', trait: 'Opportunistic' },
        },
        {
          id: 'cs-2',
          scenario: 'A colleague pitches you on a side business idea that\'s outside your expertise.',
          optionA: { label: 'I\'d need to research the market thoroughly before committing any time', trait: 'Methodical' },
          optionB: { label: 'If the person is right, I\'m in. I can learn the domain as we go', trait: 'People-First' },
        },
        {
          id: 'cs-3',
          scenario: 'You\'re offered a role that\'s a 40% pay cut but in a field you\'re passionate about.',
          optionA: { label: 'Financial stability first. I\'d transition gradually with a side project', trait: 'Pragmatic' },
          optionB: { label: 'Life is short. I\'d make the leap and figure out finances later', trait: 'Purpose-Driven' },
        },
        {
          id: 'cs-4',
          scenario: 'AI tools can now do 30% of your current job. What\'s your move?',
          optionA: { label: 'I\'d master the AI tools and become the person who trains the rest of the org', trait: 'Adaptive Leader' },
          optionB: { label: 'I\'d double down on the human-only parts: strategy, relationships, creativity', trait: 'Human-Edge' },
        },
      ],
    },
    {
      id: 'experience',
      title: 'Your story so far',
      subtitle: 'Optional: share your work history, key projects, or career highlights.',
      encouragement: 'This helps us build on what you\'ve already accomplished, not start from scratch.',
      type: 'context',
      options: [],
      allowOpenText: true,
      openTextPlaceholder: 'e.g., 5 years in product management at a Series B startup, led a team of 8, pivoted from finance to tech, built a side business doing $3K/mo in revenue...',
    },
    {
      id: 'resume-upload',
      title: 'Upload your resume',
      subtitle: 'We\'ll extract your skills & experience to personalize your path.',
      encouragement: 'Your resume data stays private and is only used to personalize your results.',
      type: 'resume-upload',
    },
    {
      id: 'personalize',
      title: 'Last thing — what does your next chapter look like?',
      subtitle: 'These answers help us match career paths to your actual life, not just your resume.',
      encouragement: 'The more honest you are here, the sharper your results will be.',
      type: 'personalize',
    },
  ],
};
